import {useEffect, useState} from 'react';

import {getAgentFoundryCanvasPreviewUrl} from './network/GetAgentFoundryCanvasPreview';
import {getCanvasDefinitionPreviewUrl} from './network/GetCanvasDefinitionPreview';
import {getCanvasFolderArtifactPreviewUrl} from './network/GetCanvasFolderArtifactPreview';
import {getCanvasVersionPreviewUrl} from './network/GetCanvasVersionPreview';
import {getPreviewErrorMessage} from './previewErrors';

export type CanvasExpandedPreviewTarget =
  | {
      canvasDefinitionId: string;
      canvasVersionId?: undefined;
      canvasFolderArtifactId?: undefined;
      agentFoundryId?: undefined;
    }
  | {
      canvasVersionId: string;
      canvasDefinitionId?: undefined;
      canvasFolderArtifactId?: undefined;
      agentFoundryId?: undefined;
    }
  | {
      canvasFolderArtifactId: string;
      canvasDefinitionId?: undefined;
      canvasVersionId?: undefined;
      agentFoundryId?: undefined;
    }
  | {
      agentFoundryId: string;
      canvasDefinitionId?: undefined;
      canvasVersionId?: undefined;
      canvasFolderArtifactId?: undefined;
    };

export interface UseCanvasExpandedPreviewUrlResult {
  signedUrl: string | null;
  isLoading: boolean;
  errorMessage: string | null;
}

export default function useCanvasExpandedPreviewUrl(
  target: CanvasExpandedPreviewTarget,
  refreshToken: number = 0
): UseCanvasExpandedPreviewUrlResult {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {canvasDefinitionId, canvasVersionId, canvasFolderArtifactId, agentFoundryId} = target;

  useEffect(() => {
    let isCancelled = false;

    setIsLoading(true);
    setErrorMessage(null);
    setSignedUrl(null);

    let fetchPreviewUrl: Promise<{signedUrl: string}>;
    if (agentFoundryId != null) {
      fetchPreviewUrl = getAgentFoundryCanvasPreviewUrl(agentFoundryId);
    } else if (canvasFolderArtifactId != null) {
      fetchPreviewUrl = getCanvasFolderArtifactPreviewUrl(canvasFolderArtifactId);
    } else if (canvasVersionId != null) {
      fetchPreviewUrl = getCanvasVersionPreviewUrl(canvasVersionId);
    } else {
      fetchPreviewUrl = getCanvasDefinitionPreviewUrl(canvasDefinitionId);
    }

    fetchPreviewUrl
      .then((response) => {
        if (!isCancelled) {
          setSignedUrl(response.signedUrl);
        }
      })
      // eslint-disable-next-line benchlint/always-throw-from-catch -- expected API errors are shown inline
      .catch((error) => {
        if (!isCancelled) {
          setErrorMessage(getPreviewErrorMessage(error));
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [agentFoundryId, canvasDefinitionId, canvasFolderArtifactId, canvasVersionId, refreshToken]);

  return {signedUrl, isLoading, errorMessage};
}
